<template>
  <div class="main-content dashboard-page" v-loading="loading">
    <div class="dashboard-container">
      <!-- 1. 顶部 Header 标题与时间筛选栏 -->
      <div
        class="dashboard-header flex justify-between align-items marginBottom20"
      >
        <div class="header-title-box">
          <div class="main-title flex align-items">
            <span class="title-accent"></span>
            <h1 class="page-title">数据看板</h1>
          </div>
          <div class="update-time-text">
            <i class="el-icon-refresh-right"></i> 数据更新时间：{{
              updateTimeText
            }}
          </div>
        </div>

        <div class="header-right-tools flex align-items">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            size="small"
            :clearable="false"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            :picker-options="pickerOptions"
            @change="handleDateRangeChange"
            class="header-date-picker"
          >
          </el-date-picker>
        </div>
      </div>

      <!-- 2. 核心指标概览卡片 (4 Cols) -->
      <el-row :gutter="16" class="marginBottom20">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-card-top flex justify-between align-items">
              <span class="stat-title">累计用户总数</span>
              <span class="stat-icon-bg teal-light"
                ><i class="el-icon-user-solid"></i
              ></span>
            </div>
            <div class="stat-number-box">
              <span class="stat-number">{{ statData.totalMemberCount || 0 }}</span>
            </div>
            <div class="stat-footer flex align-items sub-details">
              <span
                >合作伙伴
                <strong>{{
                  (statData.totalMemberCount || 0) -
                  (statData.liangxinMemberCount || 0)
                }}</strong>
                人</span
              >
              <span class="divider">/</span>
              <span
                >良信
                <strong>{{ statData.liangxinMemberCount || 0 }}</strong> 人</span
              >
            </div>
          </div>
        </el-col>

        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-card-top flex justify-between align-items">
              <span class="stat-title">总学习时长</span>
              <span class="stat-icon-bg teal-light"
                ><i class="el-icon-time"></i
              ></span>
            </div>
            <div class="stat-number-box">
              <span class="stat-number">{{ displayStudyDuration }}</span>
            </div>
            <div class="stat-footer flex align-items">
              <span class="footer-label">较上期</span>
              <span
                :class="[
                  'trend-badge',
                  isNegative(statData.studyDurationChangeRate)
                    ? 'trend-down'
                    : 'trend-up',
                ]"
              >
                <i
                  :class="
                    isNegative(statData.studyDurationChangeRate)
                      ? 'el-icon-bottom'
                      : 'el-icon-top'
                  "
                ></i>
                {{ displayStudyDurationRate }}
              </span>
            </div>
          </div>
        </el-col>

        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-card-top flex justify-between align-items">
              <span class="stat-title">总内容浏览量</span>
              <span class="stat-icon-bg teal-light"
                ><i class="el-icon-view"></i
              ></span>
            </div>
            <div class="stat-number-box">
              <span class="stat-number">{{
                formatNumber(statData.totalContentViewCount || 0)
              }}</span>
              <span class="stat-unit">次</span>
            </div>
            <div class="stat-footer flex align-items sub-details">
              <span
                >课程 <strong>{{ statData.courseViewCount || 0 }}</strong></span
              >
              <span class="divider">/</span>
              <span
                >新闻 <strong>{{ displayNewsViewCount }}</strong></span
              >
              <span class="divider">/</span>
              <span
                >手册 <strong>{{ statData.manualViewCount || 0 }}</strong></span
              >
            </div>
          </div>
        </el-col>

        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-card-top flex justify-between align-items">
              <span class="stat-title">活跃合作伙伴数</span>
              <span class="stat-icon-bg teal-light"
                ><i class="el-icon-user"></i
              ></span>
            </div>
            <div class="stat-number-box">
              <span class="stat-number">{{
                statData.activePartnerCount || 0
              }}</span>
              <span class="stat-unit">家</span>
            </div>
            <div class="stat-footer flex align-items justify-between width-100">
              <span class="footer-label"
                >共 {{ statData.partnerCount || 0 }} 家</span
              >
              <span class="trend-badge trend-neutral">
                活跃率 {{ displayActiveRate }}
              </span>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 3. 第一行：视频课程播放量 Top10 -->
      <div class="dashboard-card marginBottom20">
        <div
          class="card-header flex justify-between align-items marginBottom15"
        >
          <div class="card-header-title flex align-items">
            <i class="el-icon-video-play header-icon"></i>
            <span>视频课程播放量 Top10</span>
          </div>
        </div>
        <div
          v-if="videoData && videoData.length > 0"
          key="video-chart"
          ref="chartVideoRef"
          class="chart-box"
        ></div>
        <div
          v-else
          key="video-empty"
          class="empty-chart-box flex justify-center align-items"
        >
          <el-empty
            description="暂无视频播放排行数据"
            :image-size="80"
          ></el-empty>
        </div>
      </div>

      <!-- 4. 第二行：手册下载率 Top10 + 新闻浏览量 Top10 -->
      <el-row :gutter="16" class="marginBottom20">
        <el-col :span="12">
          <div class="dashboard-card">
            <div
              class="card-header flex justify-between align-items marginBottom15"
            >
              <div class="card-header-title flex align-items">
                <i class="el-icon-document header-icon"></i>
                <span>手册下载率 Top10</span>
              </div>
            </div>
            <div
              v-if="manualTopData && manualTopData.length > 0"
              key="manual-chart"
              ref="chartManualRef"
              class="chart-box-sm"
            ></div>
            <div
              v-else
              key="manual-empty"
              class="empty-chart-box-sm flex justify-center align-items"
            >
              <el-empty
                description="暂无手册下载数据"
                :image-size="70"
              ></el-empty>
            </div>
          </div>
        </el-col>

        <el-col :span="12">
          <div class="dashboard-card">
            <div
              class="card-header flex justify-between align-items marginBottom15"
            >
              <div class="card-header-title flex align-items">
                <i class="el-icon-news header-icon"></i>
                <span>新闻浏览量 Top10</span>
              </div>
            </div>
            <div
              v-if="newsTopData && newsTopData.length > 0"
              key="news-chart"
              ref="chartNewsRef"
              class="chart-box-sm"
            ></div>
            <div
              v-else
              key="news-empty"
              class="empty-chart-box-sm flex justify-center align-items"
            >
              <el-empty
                description="暂无新闻浏览数据"
                :image-size="70"
              ></el-empty>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 5. 第三行：合作伙伴学习时长与浏览量统计表格 -->
      <div class="dashboard-card marginBottom20">
        <div
          class="card-header flex justify-between align-items marginBottom15"
        >
          <div class="card-header-title statistics-card-title flex align-items">
            <i class="el-icon-s-data header-icon"></i>
            <span>合作伙伴学习时长与浏览量统计</span>
            <el-radio-group
              v-model="partnerFilterType"
              size="small"
              class="statistics-filter-tabs"
              @change="handlePartnerTypeChange"
            >
              <el-radio-button :label="statisticsType.ALL">全部</el-radio-button>
              <el-radio-button :label="statisticsType.PARTNER"
                >合作伙伴</el-radio-button
              >
              <el-radio-button :label="statisticsType.LIANGXIN_EMPLOYEE"
                >良信员工</el-radio-button
              >
            </el-radio-group>
          </div>
          <span class="card-extra-text">{{ partnerCountText }}</span>
        </div>

        <el-table
          :data="displayPartnerTableData"
          :row-class-name="tableRowClassName"
          size="small"
          header-cell-class-name="custom-table-header"
          class="custom-dashboard-table"
        >
          <el-table-column label="排名" width="75" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.isTotalRow" class="total-rank-dash">—</span>
              <span
                v-else
                :class="[
                  'rank-pill',
                  scope.row.rankNum <= 3
                    ? 'rank-top-' + scope.row.rankNum
                    : 'rank-normal',
                ]"
              >
                {{ scope.row.rankNum }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="groupName" label="名称">
            <template slot-scope="scope">
              <template v-if="scope.row.isTotalRow">
                <span
                  :class="[
                    'total-name-bold',
                    scope.row.groupType === 1
                      ? 'partner-total-text'
                      : 'staff-total-text',
                  ]"
                >
                  {{ scope.row.groupName }}
                </span>
              </template>
              <template v-else>
                <span class="table-company-name">{{
                  scope.row.groupName || scope.row.username
                }}</span>
                <span v-if="scope.row.groupType === 2" class="employee-badge"
                  >{{ scope.row.username }}良信员工</span
                >
              </template>
            </template>
          </el-table-column>

          <el-table-column label="总学习时长" align="center" min-width="140">
            <template slot-scope="scope">
              <span
                :class="[
                  scope.row.isTotalRow
                    ? scope.row.groupType === 1
                      ? 'partner-total-text'
                      : 'staff-total-text'
                    : 'table-duration-blue',
                ]"
              >
                {{ formatDuration(scope.row.totalStudyDuration) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="总浏览量" align="center" min-width="120">
            <template slot-scope="scope">
              <span
                :class="[
                  scope.row.isTotalRow
                    ? scope.row.groupType === 1
                      ? 'partner-total-text'
                      : 'staff-total-text'
                    : 'table-number-bold',
                ]"
              >
                {{
                  formatNumber(
                    scope.row.totalViewCount !== undefined
                      ? scope.row.totalViewCount
                      : 0
                  )
                }}
              </span>
              <span
                :class="[
                  scope.row.isTotalRow
                    ? scope.row.groupType === 1
                      ? 'partner-total-text'
                      : 'staff-total-text'
                    : 'unit-text',
                ]"
              >
                次</span
              >
            </template>
          </el-table-column>

          <el-table-column label="课程播放" align="center" min-width="100">
            <template slot-scope="scope">
              <span :class="{ 'total-number-bold': scope.row.isTotalRow }">
                {{
                  formatNumber(
                    scope.row.coursePlayCount !== undefined
                      ? scope.row.coursePlayCount
                      : 0
                  )
                }}
              </span>
              <span class="unit-text"> 次</span>
            </template>
          </el-table-column>

          <el-table-column label="课程浏览" align="center" min-width="100">
            <template slot-scope="scope">
              <span :class="{ 'total-number-bold': scope.row.isTotalRow }">
                {{
                  formatNumber(
                    scope.row.courseViewCount !== undefined
                      ? scope.row.courseViewCount
                      : 0
                  )
                }}
              </span>
              <span class="unit-text"> 次</span>
            </template>
          </el-table-column>

          <el-table-column label="新闻浏览" align="center" min-width="100">
            <template slot-scope="scope">
              <span :class="{ 'total-number-bold': scope.row.isTotalRow }">
                {{
                  formatNumber(
                    scope.row.newsViewCount !== undefined
                      ? scope.row.newsViewCount
                      : 0
                  )
                }}
              </span>
              <span class="unit-text"> 次</span>
            </template>
          </el-table-column>

          <el-table-column label="公告浏览" align="center" min-width="100">
            <template slot-scope="scope">
              <span :class="{ 'total-number-bold': scope.row.isTotalRow }">
                {{
                  formatNumber(
                    scope.row.noticeViewCount !== undefined
                      ? scope.row.noticeViewCount
                      : 0
                  )
                }}
              </span>
              <span class="unit-text"> 次</span>
            </template>
          </el-table-column>

          <el-table-column label="手册查看" align="center" min-width="100">
            <template slot-scope="scope">
              <span :class="{ 'total-number-bold': scope.row.isTotalRow }">
                {{
                  formatNumber(
                    scope.row.manualViewCount !== undefined
                      ? scope.row.manualViewCount
                      : 0
                  )
                }}
              </span>
              <span class="unit-text"> 次</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 合作伙伴统计完整后端分页器 -->
        <div
          class="pagination-box flex justify-end marginTop15"
          v-if="partnerTotalCount > 0"
        >
          <el-pagination
            @size-change="handlePartnerSizeChange"
            @current-change="handlePartnerCurrentChange"
            :current-page="partnerCurrentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="partnerPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="partnerTotalCount"
            background
          >
          </el-pagination>
        </div>
      </div>

      <!-- 6. 第四行：企业用户登录记录 -->
      <div class="dashboard-card marginBottom20">
        <div
          class="card-header flex justify-between align-items marginBottom15"
        >
          <div class="card-header-title flex align-items">
            <i class="el-icon-key header-icon"></i>
            <span>用户登入记录</span>
          </div>
          <span class="card-extra-text">共 {{ loginTotalCount }} 条记录</span>
        </div>

        <el-table
          :data="loginTableData"
          size="small"
          header-cell-class-name="custom-table-header"
          class="custom-dashboard-table"
        >
          <el-table-column
            prop="createTime"
            label="登录时间"
            width="170"
            align="center"
          >
            <template slot-scope="scope">
              <span class="time-text">{{ scope.row.createTime }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="factoryName" label="企业名称">
            <template slot-scope="scope">
              <span  class="table-company-name">{{
              scope.row.type == 1 ?  scope.row.factoryName  : '良信'
              }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="username" label="登录人"></el-table-column>
          <el-table-column prop="phone" label="手机号" align="center">
            <template slot-scope="scope">
              <span class="table-code">{{ scope.row.phone || "-" }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="roleName" label="角色" align="center">
            <template slot-scope="scope">
              <span>
                {{ scope.row.roleName || "-" }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="post" label="职位" align="center">
            <template slot-scope="scope">
              <span class="text-subtle">{{ scope.row.post || "-" }}</span>
            </template>
          </el-table-column>
        </el-table>

        <!-- 登录记录完整后端分页器 -->
        <div
          class="pagination-box flex justify-end marginTop15"
          v-if="loginTotalCount > 0"
        >
          <el-pagination
            @size-change="handleLoginSizeChange"
            @current-change="handleLoginCurrentChange"
            :current-page="loginCurrentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="loginPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="loginTotalCount"
            background
          >
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {
  getDashboardStatistics,
  getCoursePlayTop10,
  getManualDownloadRateTop10,
  getNewsViewTop10,
  getStudyDurationViewStatisticsList,
  getMemberLoginRecordList,
  getStudyDurationViewStatisticsTotal,
} from "@/api/report/dashboard";

const STATISTICS_TYPE = Object.freeze({
  ALL: 0,
  PARTNER: 1,
  LIANGXIN_EMPLOYEE: 2,
});

export default {
  name: "DashboardIndex",
  data() {
    // 快捷选项配置（所有日期均可自由选择）
    const pickerOptions = {
      shortcuts: [
        {
          text: "昨日",
          onClick(picker) {
            const now = new Date();
            const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
            const dateStr = `${yesterday.getFullYear()}-${String(
              yesterday.getMonth() + 1
            ).padStart(2, "0")}-${String(yesterday.getDate()).padStart(
              2,
              "0"
            )}`;
            picker.$emit("pick", [dateStr, dateStr]);
          },
        },
        {
          text: "近一周",
          onClick(picker) {
            const now = new Date();
            const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
            const start = new Date(yesterday.getTime() - 6 * 24 * 3600 * 1000);
            const endStr = `${yesterday.getFullYear()}-${String(
              yesterday.getMonth() + 1
            ).padStart(2, "0")}-${String(yesterday.getDate()).padStart(
              2,
              "0"
            )}`;
            const startStr = `${start.getFullYear()}-${String(
              start.getMonth() + 1
            ).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
            picker.$emit("pick", [startStr, endStr]);
          },
        },
        {
          text: "近一月",
          onClick(picker) {
            const now = new Date();
            const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
            const start = new Date(yesterday.getTime() - 29 * 24 * 3600 * 1000);
            const endStr = `${yesterday.getFullYear()}-${String(
              yesterday.getMonth() + 1
            ).padStart(2, "0")}-${String(yesterday.getDate()).padStart(
              2,
              "0"
            )}`;
            const startStr = `${start.getFullYear()}-${String(
              start.getMonth() + 1
            ).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
            picker.$emit("pick", [startStr, endStr]);
          },
        },
      ],
    };

    return {
      dateRange: [], // 时间范围：[startDate, endDate]
      updateTimeText: "", // 看板更新时间文本
      loading: false,

      // 图表 ECharts 实例引用
      chartVideo: null,
      chartManual: null,
      chartNews: null,
      resizeObserver: null,

      // 概览卡片指标数据（纯真实数据）
      statData: {
        partnerCount: 0,
        totalMemberCount: 0,
        liangxinMemberCount: 0,
        partnerCompare: 0,
        totalStudyDuration: 0,
        studyDurationChangeRate: 0,
        totalContentViewCount: 0,
        courseViewCount: 0,
        newsViewCount: 0,
        noticeViewCount: 0,
        manualViewCount: 0,
        activePartnerCount: 0,
        activePartnerRate: 0,
      },

      // 视频课程播放量 Top10 (纯真实数据)
      videoData: [],

      // 手册下载率 Top10 (纯真实数据)
      manualTopData: [],

      // 新闻浏览量 Top10 (纯真实数据)
      newsTopData: [],

      // 合作伙伴与良信员工合计列表 (纯真实数据)
      partnerTotalList: [],

      // 合作伙伴统计列表数据与分页 (纯真实数据)
      statisticsType: STATISTICS_TYPE,
      partnerFilterType: STATISTICS_TYPE.ALL,
      partnerTableData: [],
      partnerCurrentPage: 1,
      partnerPageSize: 10,
      partnerTotalCount: 0,

      // 企业用户登录记录与分页 (纯真实数据)
      loginTableData: [],
      loginCurrentPage: 1,
      loginPageSize: 10,
      loginTotalCount: 0,

      // 日期选择框配置
      pickerOptions,
    };
  },
  computed: {
    // 学习时长显示计算（秒精准转为天、小时、分、秒格式）
    displayStudyDuration() {
      if (this.statData && this.statData.totalStudyDuration) {
        return this.formatDuration(this.statData.totalStudyDuration);
      }
      return "0秒";
    },
    // 学习时长同比变化文本
    displayStudyDurationRate() {
      if (
        this.statData &&
        this.statData.studyDurationChangeRate !== undefined &&
        this.statData.studyDurationChangeRate !== null
      ) {
        return `${this.statData.studyDurationChangeRate}%`;
      }
      return "0%";
    },
    // 新闻+公告浏览量汇总
    displayNewsViewCount() {
      const news = (this.statData && this.statData.newsViewCount) || 0;
      const notice = (this.statData && this.statData.noticeViewCount) || 0;
      return news + notice;
    },
    // 活跃率计算格式化
    displayActiveRate() {
      if (
        this.statData &&
        this.statData.activePartnerRate !== undefined &&
        this.statData.activePartnerRate !== null
      ) {
        return `${this.statData.activePartnerRate}%`;
      }
      return "0%";
    },

    partnerCountText() {
      if (this.partnerFilterType === STATISTICS_TYPE.PARTNER) {
        return `共 ${this.partnerTotalCount} 家合作伙伴`;
      }
      if (this.partnerFilterType === STATISTICS_TYPE.LIANGXIN_EMPLOYEE) {
        return `共 ${this.partnerTotalCount} 位良信员工`;
      }
      return `共 ${this.partnerTotalCount} 条数据`;
    },

    /**
     * 合作伙伴与良信员工学习统计表格渲染数据（整合固定置顶的合计行和明细分页数据）
     *
     * @returns {Array<Object>} 组合后的表格数据数组
     */
    displayPartnerTableData() {
      const result = [];
      // 1. 合计行数据处理
      if (
        Array.isArray(this.partnerTotalList) &&
        this.partnerTotalList.length > 0
      ) {
        this.partnerTotalList
          .filter(
            (item) =>
              this.partnerFilterType === STATISTICS_TYPE.ALL ||
              Number(item.groupType) === this.partnerFilterType
          )
          .forEach((item) => {
            result.push({
              ...item,
              isTotalRow: true,
              groupName:
                item.groupName ||
                (item.groupType === 1
                  ? "合作伙伴合计"
                  : item.groupType === 2
                  ? "良信员工合计"
                  : "合计"),
            });
          });
      }
      // 2. 明细数据列表处理（带有全局准确排名的 rankNum）
      if (Array.isArray(this.partnerTableData)) {
        this.partnerTableData.forEach((item, index) => {
          const rankNum =
            (this.partnerCurrentPage - 1) * this.partnerPageSize + index + 1;
          result.push({
            ...item,
            isTotalRow: false,
            rankNum,
          });
        });
      }
      return result;
    },
  },
  mounted() {
    this.initDashboard();
    window.addEventListener("resize", this.handleResize);
    // 监听根容器尺寸变化（解决页面刷新、侧边栏折叠/展开、布局异步就绪时的尺寸失真）
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      if (this.$el) {
        this.resizeObserver.observe(this.$el);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.chartVideo) this.chartVideo.dispose();
    if (this.chartManual) this.chartManual.dispose();
    if (this.chartNews) this.chartNews.dispose();
  },
  methods: {
    /**
     * 初始化数据看板（设置默认【近一周】日期并拉取数据）
     *
     * @returns {void}
     */
    initDashboard() {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
      const start = new Date(yesterday.getTime() - 6 * 24 * 3600 * 1000);
      const endStr = this.formatDate(yesterday);
      const startStr = this.formatDate(start);

      this.dateRange = [startStr, endStr];
      this.updateTimeText = this.getFormatNowTime();
      this.fetchDashboardData();
    },

    /**
     * 手动更改 el-date-picker 日期范围或使用快捷选项时的响应逻辑
     *
     * @param {Array<string>} val - 选中的日期范围数组
     * @returns {void}
     */
    handleDateRangeChange(val) {
      if (!val || val.length === 0) {
        const now = new Date();
        const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
        const start = new Date(yesterday.getTime() - 6 * 24 * 3600 * 1000);
        this.dateRange = [this.formatDate(start), this.formatDate(yesterday)];
      }
      this.partnerCurrentPage = 1;
      this.loginCurrentPage = 1;
      this.fetchDashboardData();
    },

    /**
     * 并行异步调用《后台新接口文档.md》全套数据看板真实 API 接口
     *
     * @returns {void}
     */
    fetchDashboardData() {
      this.loading = true;
      const startDate =
        this.dateRange && this.dateRange[0] ? this.dateRange[0] : "";
      const endDate =
        this.dateRange && this.dateRange[1] ? this.dateRange[1] : "";
      const startTime = startDate ? `${startDate} 00:00:00` : "";
      const endTime = endDate ? `${endDate} 23:59:59` : "";

      const queryParams = { startTime, endTime, startDate, endDate };

      Promise.allSettled([
        getDashboardStatistics(queryParams),
        getCoursePlayTop10(queryParams),
        getManualDownloadRateTop10(queryParams),
        getNewsViewTop10(queryParams),
        getStudyDurationViewStatisticsList({
          ...queryParams,
          page: this.partnerCurrentPage,
          size: this.partnerPageSize,
          ...(this.partnerFilterType === STATISTICS_TYPE.ALL
            ? {}
            : { type: this.partnerFilterType }),
        }),
        getMemberLoginRecordList({
          ...queryParams,
          page: this.loginCurrentPage,
          size: this.loginPageSize,
        }),
        getStudyDurationViewStatisticsTotal(queryParams),
      ]).then((results) => {
        this.loading = false;

        // 1. 统计概览
        const resStat = results[0];
        if (
          resStat.status === "fulfilled" &&
          resStat.value &&
          (resStat.value.code === 200 || resStat.value.code === 0) &&
          resStat.value.data
        ) {
          this.statData = resStat.value.data;
        } else {
          this.statData = {};
        }

        // 2. 视频 Top10
        const resVideo = results[1];
        if (
          resVideo.status === "fulfilled" &&
          resVideo.value &&
          (resVideo.value.code === 200 || resVideo.value.code === 0) &&
          Array.isArray(resVideo.value.data)
        ) {
          this.videoData = resVideo.value.data;
        } else {
          this.videoData = [];
        }

        // 3. 手册下载率 Top10
        const resManual = results[2];
        if (
          resManual.status === "fulfilled" &&
          resManual.value &&
          (resManual.value.code === 200 || resManual.value.code === 0) &&
          Array.isArray(resManual.value.data)
        ) {
          this.manualTopData = resManual.value.data;
        } else {
          this.manualTopData = [];
        }

        // 4. 新闻浏览量 Top10
        const resNews = results[3];
        if (
          resNews.status === "fulfilled" &&
          resNews.value &&
          (resNews.value.code === 200 || resNews.value.code === 0) &&
          Array.isArray(resNews.value.data)
        ) {
          this.newsTopData = resNews.value.data;
        } else {
          this.newsTopData = [];
        }

        // 5. 合作伙伴统计表格
        const resPartner = results[4];
        if (
          resPartner.status === "fulfilled" &&
          resPartner.value &&
          (resPartner.value.code === 200 || resPartner.value.code === 0) &&
          resPartner.value.data
        ) {
          const pData = resPartner.value.data;
          const records = pData.page ? pData.page.records : pData.records || [];
          this.partnerTableData = Array.isArray(records) ? records : [];
          this.partnerTotalCount = pData.page
            ? pData.page.total || 0
            : pData.total || this.partnerTableData.length;
        } else {
          this.partnerTableData = [];
          this.partnerTotalCount = 0;
        }

        // 6. 登录记录表格
        const resLogin = results[5];
        if (
          resLogin.status === "fulfilled" &&
          resLogin.value &&
          (resLogin.value.code === 200 || resLogin.value.code === 0) &&
          resLogin.value.data
        ) {
          const lData = resLogin.value.data;
          const records =
            lData.records || (lData.page ? lData.page.records : []);
          this.loginTableData = Array.isArray(records) ? records : [];
          this.loginTotalCount =
            lData.total !== undefined
              ? lData.total
              : lData.page
              ? lData.page.total
              : this.loginTableData.length;
        } else {
          this.loginTableData = [];
          this.loginTotalCount = 0;
        }

        // 7. 合作伙伴与良信员工合计数据
        const resTotal = results[6];
        if (
          resTotal &&
          resTotal.status === "fulfilled" &&
          resTotal.value &&
          (resTotal.value.code === 200 || resTotal.value.code === 0) &&
          Array.isArray(resTotal.value.data)
        ) {
          this.partnerTotalList = resTotal.value.data;
        } else {
          this.partnerTotalList = [];
        }

        this.$nextTick(() => {
          this.initCharts();
        });
      });
    },

    /**
     * 合作伙伴统计列表单独分页查询
     *
     * @param {number} [page] - 目标页码
     * @param {number} [size] - 每页条数
     * @returns {void}
     */
    fetchPartnerList(page, size) {
      if (page) this.partnerCurrentPage = page;
      if (size) this.partnerPageSize = size;
      const startDate =
        this.dateRange && this.dateRange[0] ? this.dateRange[0] : "";
      const endDate =
        this.dateRange && this.dateRange[1] ? this.dateRange[1] : "";
      const startTime = startDate ? `${startDate} 00:00:00` : "";
      const endTime = endDate ? `${endDate} 23:59:59` : "";

      getStudyDurationViewStatisticsList({
        startTime,
        endTime,
        startDate,
        endDate,
        page: this.partnerCurrentPage,
        size: this.partnerPageSize,
        ...(this.partnerFilterType === STATISTICS_TYPE.ALL
          ? {}
          : { type: this.partnerFilterType }),
      })
        .then((res) => {
          if (res && (res.code === 200 || res.code === 0) && res.data) {
            const pData = res.data;
            const records = pData.page
              ? pData.page.records
              : pData.records || [];
            this.partnerTableData = Array.isArray(records) ? records : [];
            this.partnerTotalCount = pData.page
              ? pData.page.total || 0
              : pData.total || this.partnerTableData.length;
          }
        })
        .catch(() => {});
    },

    /**
     * 合作伙伴统计列表 - 页码切换
     *
     * @param {number} page - 目标页码
     * @returns {void}
     */
    handlePartnerCurrentChange(page) {
      this.fetchPartnerList(page, null);
    },

    /**
     * 切换统计对象后回到第一页，并按类型重新加载明细数据。
     *
     * @returns {void}
     */
    handlePartnerTypeChange() {
      this.partnerCurrentPage = 1;
      this.fetchPartnerList(1, null);
    },

    /**
     * 合作伙伴统计列表 - 每页条数切换
     *
     * @param {number} size - 每页条数
     * @returns {void}
     */
    handlePartnerSizeChange(size) {
      this.partnerCurrentPage = 1;
      this.fetchPartnerList(1, size);
    },

    /**
     * 单独刷新登录记录列表（真后端分页）
     *
     * @param {number} [page] - 目标页码
     * @param {number} [size] - 每页条数
     * @returns {void}
     */
    fetchLoginRecordList(page, size) {
      if (page) this.loginCurrentPage = page;
      if (size) this.loginPageSize = size;
      const startDate =
        this.dateRange && this.dateRange[0] ? this.dateRange[0] : "";
      const endDate =
        this.dateRange && this.dateRange[1] ? this.dateRange[1] : "";
      const startTime = startDate ? `${startDate} 00:00:00` : "";
      const endTime = endDate ? `${endDate} 23:59:59` : "";

      getMemberLoginRecordList({
        startTime,
        endTime,
        startDate,
        endDate,
        page: this.loginCurrentPage,
        size: this.loginPageSize,
      })
        .then((res) => {
          if (res && (res.code === 200 || res.code === 0) && res.data) {
            const records =
              res.data.records || (res.data.page ? res.data.page.records : []);
            this.loginTableData = Array.isArray(records) ? records : [];
            this.loginTotalCount =
              res.data.total !== undefined
                ? res.data.total
                : res.data.page
                ? res.data.page.total
                : this.loginTableData.length;
          }
        })
        .catch(() => {});
    },

    /**
     * 用户登录记录 - 页码切换
     *
     * @param {number} page - 目标页码
     * @returns {void}
     */
    handleLoginCurrentChange(page) {
      this.fetchLoginRecordList(page, null);
    },

    /**
     * 用户登录记录 - 每页条数切换
     *
     * @param {number} size - 每页条数
     * @returns {void}
     */
    handleLoginSizeChange(size) {
      this.loginCurrentPage = 1;
      this.fetchLoginRecordList(1, size);
    },

    /**
     * 获取排名 Badge 的专属样式 Class
     *
     * @param {number} rankIndex - 0-based 排名索引
     * @returns {string} 样式类名
     */
    getRankBadgeClass(rankIndex) {
      if (rankIndex === 0) return "rank-badge rank-1";
      if (rankIndex === 1) return "rank-badge rank-2";
      if (rankIndex === 2) return "rank-badge rank-3";
      return "rank-badge rank-other";
    },

    /**
     * 初始化/重绘所有 ECharts 图表
     * 内部结合微任务与宏任务多阶段延时触发 resize，确保页面首次刷新及 DOM 宽度稳定后图表尺寸 100% 精准贴合
     *
     * @returns {void}
     */
    initCharts() {
      this.$nextTick(() => {
        if (this.videoData && this.videoData.length > 0) {
          this.renderVideoChart();
        }
        if (this.manualTopData && this.manualTopData.length > 0) {
          this.renderManualChart();
        }
        if (this.newsTopData && this.newsTopData.length > 0) {
          this.renderNewsChart();
        }
        // 延时重置尺寸，防止页面初次加载时由于 CSS/Flex 布局计算未就绪导致的图表宽度溢出
        setTimeout(() => {
          this.handleResize();
        }, 50);
        setTimeout(() => {
          this.handleResize();
        }, 200);
      });
    },

    /**
     * 渲染视频课程播放量 Top10 柱状图
     *
     * @returns {void}
     */
    renderVideoChart() {
      if (!this.$refs.chartVideoRef) return;
      if (this.chartVideo) {
        if (this.chartVideo.getDom() !== this.$refs.chartVideoRef) {
          this.chartVideo.dispose();
          this.chartVideo = echarts.init(this.$refs.chartVideoRef);
        }
      } else {
        this.chartVideo = echarts.init(this.$refs.chartVideoRef);
      }
      const names = this.videoData.map(
        (d) => d.courseTitle || d.title || d.name || ""
      );
      const values = this.videoData.map((d) =>
        d.playCount !== undefined ? d.playCount : 0
      );

      const option = {
        grid: { left: 15, right: 15, top: 35, bottom: 25, containLabel: true },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          backgroundColor: "#ffffff",
          borderColor: "#e5e7eb",
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: "#1f2937", fontSize: 12 },
          extraCssText:
            "box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); border-radius: 8px;",
          formatter: (params) => {
            const p = params[0];
            return `<div style="font-weight:600;margin-bottom:4px;color:#111827;">${p.name}</div>
                    <div style="color:#4b5563;font-size:12px;">播放次数: <strong style="color:#00B5A3;font-size:14px;margin-left:4px;">${p.value}</strong> 次</div>`;
          },
        },
        xAxis: {
          type: "category",
          data: names,
          axisLine: { lineStyle: { color: "#e5e7eb" } },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            rotate: 30,
            align: "right",
            verticalAlign: "top",
            interval: 0,
            color: "#6b7280",
            margin: 10,
            formatter: (v) => (v && v.length > 6 ? v.substring(0, 6) + "..." : v),
          },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          max: (value) => (value.max === 0 ? 5 : Math.ceil(value.max * 1.18)),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: "dashed", color: "#f3f4f6" } },
          axisLabel: { fontSize: 11, color: "#9ca3af" },
        },
        series: [
          {
            type: "bar",
            data: values,
            barWidth: 18,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#00D8C3" },
                { offset: 1, color: "#00B5A3" },
              ]),
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#00c4b1" },
                  { offset: 1, color: "#009d8e" },
                ]),
              },
            },
            label: {
              show: true,
              position: "top",
              fontSize: 11,
              fontWeight: "600",
              color: "#00B5A3",
            },
          },
        ],
      };
      this.chartVideo.setOption(option, true);
      this.chartVideo.resize();
    },

    /**
     * 渲染手册下载率 Top10 横向条形图
     *
     * @returns {void}
     */
    renderManualChart() {
      if (!this.$refs.chartManualRef) return;
      if (this.chartManual) {
        if (this.chartManual.getDom() !== this.$refs.chartManualRef) {
          this.chartManual.dispose();
          this.chartManual = echarts.init(this.$refs.chartManualRef);
        }
      } else {
        this.chartManual = echarts.init(this.$refs.chartManualRef);
      }
      const sortedManual = [...this.manualTopData].sort((a, b) => {
        const rateA = a.downloadRate !== undefined ? a.downloadRate : 0;
        const rateB = b.downloadRate !== undefined ? b.downloadRate : 0;
        return rateA - rateB;
      });

      const maxRate = sortedManual.reduce((max, d) => {
        const val = Number(d.downloadRate) || 0;
        return val > max ? val : max;
      }, 0);
      const axisMax = maxRate > 100 ? Math.ceil(maxRate / 10) * 10 : 100;

      const option = {
        grid: { left: 15, right: 90, top: 15, bottom: 15, containLabel: true },
        tooltip: {
          // 按分类行触发，确保下载率为 0、柱体无宽度时仍可查看详情。
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          backgroundColor: "#ffffff",
          borderColor: "#e5e7eb",
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: "#1f2937", fontSize: 12 },
          extraCssText:
            "box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); border-radius: 8px;",
          formatter: (params) => {
            const p = Array.isArray(params) ? params[0] : params;
            const d = sortedManual[p.dataIndex] || {};
            const rateVal = d.downloadRate !== undefined ? d.downloadRate : 0;
            const down = d.downloadCount !== undefined ? d.downloadCount : 0;
            const view = d.viewCount !== undefined ? d.viewCount : 0;
            return `<div style="font-weight:600;margin-bottom:4px;color:#111827;">${
              d.title || d.name || ""
            }</div>
                    <div style="color:#4b5563;font-size:12px;">下载率: <strong style="color:#00B5A3;font-size:13px;">${rateVal}%</strong></div>
                    <div style="color:#9ca3af;font-size:11px;margin-top:2px;">下载 ${down} 次 / 查看 ${view} 次</div>`;
          },
        },
        xAxis: {
          type: "value",
          min: 0,
          max: axisMax,
          interval: axisMax === 100 ? 20 : undefined,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: "dashed", color: "#f3f4f6" } },
          axisLabel: { fontSize: 11, formatter: "{value}%", color: "#9ca3af" },
        },
        yAxis: {
          type: "category",
          data: sortedManual.map((d) => d.title || d.name || ""),
          axisLine: { lineStyle: { color: "#e5e7eb" } },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            color: "#4b5563",
            margin: 10,
            formatter: (v) => (v && v.length > 7 ? v.substring(0, 7) + "..." : v),
          },
        },
        series: [
          {
            type: "bar",
            showBackground: true,
            backgroundStyle: { color: "#f0fdfb", borderRadius: [0, 4, 4, 0] },
            data: sortedManual.map((d) => {
              const rateVal = d.downloadRate !== undefined ? d.downloadRate : 0;
              return {
                value: rateVal,
                itemStyle: {
                  borderRadius: [0, 4, 4, 0],
                  color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: "#36cfc9" },
                    { offset: 1, color: "#00D8C3" },
                  ]),
                },
              };
            }),
            barWidth: 12,
            label: {
              show: true,
              position: "right",
              fontSize: 11,
              fontWeight: "600",
              color: "#00B5A3",
              formatter: (p) => {
                const item = sortedManual[p.dataIndex] || {};
                const down =
                  item.downloadCount !== undefined ? item.downloadCount : 0;
                const view = item.viewCount !== undefined ? item.viewCount : 0;
                return `${p.value}% (${down}/${view})`;
              },
            },
          },
        ],
      };
      this.chartManual.setOption(option, true);
      this.chartManual.resize();
    },

    /**
     * 渲染新闻浏览量 Top10 柱状图
     *
     * @returns {void}
     */
    renderNewsChart() {
      if (!this.$refs.chartNewsRef) return;
      if (this.chartNews) {
        if (this.chartNews.getDom() !== this.$refs.chartNewsRef) {
          this.chartNews.dispose();
          this.chartNews = echarts.init(this.$refs.chartNewsRef);
        }
      } else {
        this.chartNews = echarts.init(this.$refs.chartNewsRef);
      }
      const names = this.newsTopData.map((d) => d.title || d.name || "");
      const values = this.newsTopData.map((d) =>
        d.viewCount !== undefined ? d.viewCount : 0
      );

      const option = {
        grid: { left: 15, right: 15, top: 35, bottom: 25, containLabel: true },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          confine: true,
          backgroundColor: "#ffffff",
          borderColor: "#e5e7eb",
          borderWidth: 1,
          padding: [10, 14],
          textStyle: { color: "#1f2937", fontSize: 12 },
          extraCssText:
            "box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); border-radius: 8px;",
          formatter: (params) => {
            const p = params[0];
            return `<div style="font-weight:600;margin-bottom:4px;color:#111827;">${p.name}</div>
                    <div style="color:#4b5563;font-size:12px;">浏览次数: <strong style="color:#00B5A3;font-size:14px;margin-left:4px;">${p.value}</strong> 次</div>`;
          },
        },
        xAxis: {
          type: "category",
          data: names,
          axisLine: { lineStyle: { color: "#e5e7eb" } },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            rotate: 30,
            align: "right",
            verticalAlign: "top",
            interval: 0,
            color: "#6b7280",
            margin: 10,
            formatter: (v) => (v && v.length > 6 ? v.substring(0, 6) + "..." : v),
          },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          max: (value) => (value.max === 0 ? 5 : Math.ceil(value.max * 1.18)),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: "dashed", color: "#f3f4f6" } },
          axisLabel: { fontSize: 11, color: "#9ca3af" },
        },
        series: [
          {
            type: "bar",
            data: values,
            barWidth: 18,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#00D8C3" },
                { offset: 1, color: "#00B5A3" },
              ]),
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: "#00c4b1" },
                  { offset: 1, color: "#009d8e" },
                ]),
              },
            },
            label: {
              show: true,
              position: "top",
              fontSize: 11,
              fontWeight: "600",
              color: "#00B5A3",
            },
          },
        ],
      };
      this.chartNews.setOption(option, true);
      this.chartNews.resize();
    },

    /**
     * 窗口大小改变或容器尺寸变化时的图表自适应响应函数
     *
     * @returns {void}
     */
    handleResize() {
      if (this.chartVideo) {
        this.chartVideo.resize();
      }
      if (this.chartManual) {
        this.chartManual.resize();
      }
      if (this.chartNews) {
        this.chartNews.resize();
      }
    },

    /**
     * 格式化 Date 为 yyyy-MM-dd
     *
     * @param {Date} date - 日期对象
     * @returns {string} 格式化字符串
     */
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    /**
     * 格式化千分位数值
     *
     * @param {number|string} val - 待格式化数字
     * @returns {string} 带千分位的格式化文本
     */
    formatNumber(val) {
      if (val === undefined || val === null || val === "") return "0";
      return Number(val).toLocaleString();
    },

    /**
     * 将总学习时长（秒）转换为精准时长格式文本（兼容保留）
     *
     * @param {number|string} totalSeconds - 待转换的总秒数
     * @returns {string} 格式化后的时长文本，例如 "2分5秒"、"1小时20分15秒"、"45秒" 或 "0秒"
     */
    formatStudyDurationMinutes(totalSeconds) {
      return this.formatDuration(totalSeconds);
    },

    /**
     * 设置合作伙伴统计表格各行的自定义 Class 类名（用于突出显示合计行背景）
     *
     * @param {Object} param0 - Element 表格行回调对象
     * @param {Object} param0.row - 当前行的数据对象
     * @returns {string} 对应行的 CSS 样式类名
     */
    tableRowClassName({ row }) {
      if (row.isTotalRow) {
        if (row.groupType === 1) {
          return "total-row-partner";
        } else if (row.groupType === 2) {
          return "total-row-staff";
        }
        return "total-row";
      }
      return "";
    },

    /**
     * 将秒数精确转换为天、小时、分钟、秒格式的文本
     *
     * @param {number|string} totalSeconds - 待转换的秒数
     * @returns {string} 格式化后的时长文本，如 "2天3小时15分20秒"、"15分30秒" 或 "45秒"
     */
    formatDuration(totalSeconds) {
      const sec = Number(totalSeconds);
      if (!sec || isNaN(sec) || sec <= 0) return "0秒";

      const d = Math.floor(sec / 86400);
      const h = Math.floor((sec % 86400) / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = Math.floor(sec % 60);

      const parts = [];
      if (d > 0) parts.push(`${d}天`);
      if (h > 0) parts.push(`${h}小时`);
      if (m > 0) parts.push(`${m}分`);
      if (s > 0 || parts.length === 0) parts.push(`${s}秒`);

      return parts.join("");
    },

    /**
     * 将秒数转换为带 HTML 节点单位的格式化文本，供概览卡片使用小字单位样式渲染
     *
     * @param {number|string} totalSeconds - 待转换的秒数
     * @returns {string} 包含小字单位样式的 HTML 格式文本
     */
    formatDurationHtml(totalSeconds) {
      const sec = Number(totalSeconds);
      const unitStyle = `style="font-size: 12px; color: #9ca3af; font-weight: 400; margin-left: 2px;"`;
      if (!sec || isNaN(sec) || sec <= 0) {
        return `0<span ${unitStyle}>秒</span>`;
      }

      const d = Math.floor(sec / 86400);
      const h = Math.floor((sec % 86400) / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = Math.floor(sec % 60);

      const parts = [];
      if (d > 0) parts.push(`${d}<span ${unitStyle}>天</span>`);
      if (h > 0) parts.push(`${h}<span ${unitStyle}>小时</span>`);
      if (m > 0) parts.push(`${m}<span ${unitStyle}>分</span>`);
      if (s > 0 || parts.length === 0)
        parts.push(`${s}<span ${unitStyle}>秒</span>`);

      return parts.join("");
    },

    /**
     * 判断传入的指标变动值是否为负数
     * 支持传入数值类型以及包含百分号、负号的字符串格式
     *
     * @param {number|string} val - 待检测的指标变动值，如 -5、"-3"、"-12.5%" 等
     * @returns {boolean} 如果为负数返回 true，否则返回 false
     */
    isNegative(val) {
      if (val === undefined || val === null || val === "") return false;
      if (typeof val === "number") return val < 0;
      const num = parseFloat(String(val).replace(/%/g, "").trim());
      return !isNaN(num) && num < 0;
    },

    /**
     * 获取当前格式化系统时间文本
     *
     * @returns {string} yyyy/MM/dd HH:mm 格式文本
     */
    getFormatNowTime() {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1;
      const d = now.getDate();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      return `${y}/${m}/${d} ${hh}:${mm}`;
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  padding: 20px 24px;
  background-color: #f8fafc;
  min-height: calc(100vh - 90px);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #1f2937;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.dashboard-container {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  min-width: 0;
}

/* 1. Header 标题栏 */
.dashboard-header {
  padding-bottom: 4px;
}

.main-title {
  gap: 10px;
}

.title-accent {
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #00d8c3 0%, #00b5a3 100%);
  border-radius: 4px;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  letter-spacing: -0.3px;
}

.update-time-text {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

.update-time-text i {
  color: #9ca3af;
  margin-right: 2px;
}

.header-date-picker {
  border-radius: 6px !important;
}

/* 2. 核心指标概览卡片 */
.stat-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 18px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid #eaeff5;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 124px;
  box-sizing: border-box;
}

.stat-card:hover {
  box-shadow: 0 8px 20px rgba(0, 216, 195, 0.08);
  border-color: #b3f5ed;
}

.stat-title {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.stat-icon-bg {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.teal-light {
  background: #e6fbf8;
  color: #00b5a3;
}

.stat-number-box {
  margin: 6px 0 2px 0;
}

.stat-number {
  font-size: 26px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.1;
}

.stat-unit {
  font-size: 12px;
  color: #9ca3af;
  margin-left: 4px;
  font-weight: 400;
}

.stat-footer {
  font-size: 12px;
  color: #6b7280;
}

.footer-label {
  margin-right: 6px;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
}

.trend-up {
  background: #e6fbf8;
  color: #00b5a3;
}

.trend-down {
  background: #fef2f2;
  color: #ef4444;
}

.trend-neutral {
  background: #f3f4f6;
  color: #4b5563;
}

.sub-details {
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
}

.sub-details strong {
  color: #111827;
}

.sub-details .divider {
  color: #d1d5db;
}

/* 3. 看板通用 Card */
.dashboard-card {
  background: #ffffff;
  border-radius: 10px;
  padding: 20px 22px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid #eaeff5;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.card-header-title {
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.header-icon {
  width: 28px;
  height: 28px;
  background: #e6fbf8;
  color: #00b5a3;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.card-extra-text {
  font-size: 12px;
  color: #9ca3af;
}

.statistics-filter-tabs {
  display: inline-flex;
  margin-left: 20px;
  padding: 3px;
  background: #f3f7f7;
  border: 1px solid #e8efef;
  border-radius: 9px;
}

.statistics-filter-tabs ::v-deep .el-radio-button__inner {
  min-width: 76px;
  height: 30px;
  padding: 0 16px;
  line-height: 30px;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border: 0 !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  transition: color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;
}

.statistics-filter-tabs ::v-deep .el-radio-button__inner:hover {
  color: #00a896;
}

.statistics-filter-tabs
  ::v-deep
  .el-radio-button__orig-radio:checked
  + .el-radio-button__inner {
  color: #008f83;
  font-weight: 600;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 143, 131, 0.14) !important;
}

.statistics-filter-tabs
  ::v-deep
  .el-radio-button__orig-radio:focus
  + .el-radio-button__inner {
  box-shadow: 0 0 0 2px rgba(0, 181, 163, 0.2) !important;
}

.statistics-card-title {
  flex-wrap: wrap;
  row-gap: 8px;
}

/* 4. ECharts 容器 */
.chart-box {
  height: 320px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.chart-box-sm {
  height: 300px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.empty-chart-box,
.empty-chart-box-sm {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #fafafa;
  border-radius: 6px;
}

.empty-chart-box {
  height: 320px;
}

.empty-chart-box-sm {
  height: 300px;
}

/* 5. 大厂级精致 Table 系统 */
.custom-dashboard-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #f3f4f6;
}

.custom-dashboard-table ::v-deep th.custom-table-header {
  background-color: #f9fafb !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  height: 42px;
  border-bottom: 1px solid #e5e7eb !important;
}

.custom-dashboard-table ::v-deep td {
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
}

.custom-dashboard-table ::v-deep .el-table__row:hover > td {
  background-color: #f0fdfb !important;
}

/* 排名徽章 Rank Pill */
.rank-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
}

.rank-top-1 {
  background: linear-gradient(135deg, #ff7a45, #ff4d4f);
  color: #ffffff;
}

.rank-top-2 {
  background: linear-gradient(135deg, #00d8c3, #00b5a3);
  color: #ffffff;
}

.rank-top-3 {
  background: linear-gradient(135deg, #34d399, #059669);
  color: #ffffff;
}

.rank-normal {
  background: #f3f4f6;
  color: #6b7280;
}

/* 表格文本排版 */
.table-company-name {
  font-weight: 600;
  color: #111827;
  font-size: 13px;
}

.table-duration-pill {
  background: #e6fbf8;
  color: #00b5a3;
  font-weight: 700;
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.unit-text {
  font-size: 11px;
  font-weight: 400;
  color: #6b7280;
}

.table-number-bold {
  font-weight: 700;
  color: #111827;
}

.table-code {
  font-family: SFMono-Regular, Consolas, Monaco, monospace;
  font-size: 12px;
  color: #4b5563;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
}

.time-text {
  font-size: 12px;
  color: #6b7280;
}

/* 合计行专属背景颜色 */
.custom-dashboard-table ::v-deep .el-table__row.total-row-partner {
  background-color: #f0f7ff !important;
}

.custom-dashboard-table ::v-deep .el-table__row.total-row-staff {
  background-color: #f6ffed !important;
}

.custom-dashboard-table ::v-deep .el-table__row.total-row-partner:hover > td {
  background-color: #e6f4ff !important;
}

.custom-dashboard-table ::v-deep .el-table__row.total-row-staff:hover > td {
  background-color: #eaffd0 !important;
}

/* 合作伙伴与良信员工合计列文本高亮 */
.total-rank-dash {
  color: #9ca3af;
  font-weight: bold;
}

.total-name-bold {
  font-size: 13px;
  font-weight: 700;
}

.partner-total-text {
  color: #0076fe !important;
  font-weight: 700 !important;
}

.staff-total-text {
  color: #52c41a !important;
  font-weight: 700 !important;
}

.total-number-bold {
  color: #111827;
  font-weight: 700;
}

.table-duration-blue {
  color: #0076fe;
  font-weight: 700;
}

/* 员工标签徽章 */
.employee-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  background-color: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
  font-size: 11px;
  font-weight: normal;
}

.text-subtle {
  color: #6b7280;
  font-size: 12px;
}

/* 角色标签 Role Tag */
.role-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.role-admin {
  background: #fef2f2;
  color: #ef4444;
  border: 1px solid #fecaca;
}

.role-user {
  background: #e6fbf8;
  color: #00b5a3;
  border: 1px solid #b3f5ed;
}

/* Pagination 重置主题 */
.pagination-box
  ::v-deep
  .el-pagination.is-background
  .el-pager
  li:not(.disabled).active {
  background-color: #00d8c3 !important;
  color: #ffffff;
}

.pagination-box ::v-deep .el-pagination.is-background .el-pager li:hover {
  color: #00d8c3;
}

/* Common Flex 工具类 */
.flex {
  display: flex;
}

.align-items {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.width-100 {
  width: 100%;
}

.marginBottom15 {
  margin-bottom: 15px;
}

.marginBottom20 {
  margin-bottom: 20px;
}

.marginTop15 {
  margin-top: 15px;
}
</style>
